import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

import Typography from '@components/ui/Typography';

import { cn } from '@utils/cn';

interface SnackbarProps {
  text: string;
  renderIcon?: boolean;
  error?: boolean;
}

export default function Snackbar({ text, renderIcon, error }: SnackbarProps) {
  const messageRef = useRef<HTMLElement>(null);
  const [width, setWidth] = useState(text.length * 16);
  const [show, setShow] = useState(false);

  useEffect(() => {
    setShow(true);
  }, []);

  useEffect(() => {
    if (messageRef.current) {
      const textWidth = messageRef.current?.offsetWidth;
      setWidth(renderIcon ? textWidth + 72 : textWidth + 48);
    }
  }, [messageRef, renderIcon]);

  const iconLeft = show ? `${width - 48}px` : `${width / 4 + 12}px`;

  return (
    <div
      className={cn(
        'transition-all duration-600 opacity-100 delay-[2500ms] fixed bottom-8 z-[999999]',
        show && 'opacity-0'
      )}
    >
      <div
        className={cn(
          'transition-all duration-300 opacity-0 w-screen grid justify-items-center',
          show && 'opacity-100'
        )}
      >
        <div
          style={{
            width: `${width}px`,
          }}
          className={cn(
            'relative sm:!h-12 sm:!min-h-12 overflow-x-hidden overflow-y-hidden rounded-full min-h-12'
          )}
        >
          <div className="absolute">
            <div
              style={{
                left: show ? '0px' : `${width}px`,
                paddingRight: `${width / 2}px`,
              }}
              className={cn(
                'transition-all duration-300 delay-[900ms] bg-transparent relative py-3 pl-6 text-nowrap rounded-full min-h-12 min-w-max',
                show && 'bg-onSurface',
                error && 'bg-error'
              )}
            >
              <Typography
                ref={messageRef}
                intent="bodyMedium"
                className={cn('text-inverseOnSurface min-w-max text-nowrap')}
              >
                {text}
              </Typography>
            </div>
          </div>
          <div className="absolute">
            <div
              style={{
                left: iconLeft,
              }}
              className={cn(
                'relative flex items-center z-10 rounded-full bg-onSurface p-3 transition-all delay-[900ms] duration-300',
                error && 'bg-error',
                show && 'bg-transparent'
              )}
            >
              {renderIcon ? (
                <Image
                  src="/icons/whiteCheck.svg"
                  width={24}
                  height={24}
                  alt="message sent"
                  className="size-6"
                />
              ) : (
                <div className="size-6" />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
